import { type ChangeEvent, type ClipboardEvent, useEffect, useCallback, useRef } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { Flex } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import { ListItemsInputView } from "app/components/React/list-editor/ListItemsInput";
import type { AutoComplete } from "app/components/React/Tooltip/AutoComplete/AutoComplete";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { getGroupName } from "app/pages/keyword-analysis/utils/getGroupName";

import { createKeyword } from "./createKeyword";
import { useKeywordGroupNameContext } from "./KeywordGroupNameContext";
import { useKeywordListComposerContext } from "./KeywordListComposerContext";
import { trackPopupClick } from "./tracking";
import { type KeywordsVolumesResponse, useKeywordsVolumes } from "./useKeywordsVolumes";

const ListItemsInputViewWrapper = styled.div`
    flex-grow: 1;

    & .add-list-item p {
        left: 0;
    }

    & .add-new-list-item {
        margin: 0;
    }

    & .customCategoriesWizard-add-domain-bar {
        margin: 0 16px 0 -32px;
    }
`;

export const KeywordListComposerInput = () => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const {
        country,
        query,
        changeQuery,
        keywords,
        loadingKeywords,
        prependKeywords,
        updateKeywordsVolumes,
        checkIfKeywordExists,
    } = useKeywordListComposerContext();
    const { keywordGroupName, changeKeywordGroupName } = useKeywordGroupNameContext();
    const itemsInputRef = useRef<AutoComplete>(null);

    const showToast = (text: string) => {
        dispatch(showSuccessToast(text));
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        changeQuery(newQuery);
    };

    const handleSubmit = async () => {
        const newKeyword = createKeyword({
            term: query,
            isSeed: keywords.length === 0,
            isLoading: true,
            isRecentlyAdded: true,
        });
        const isExistingKeyword = checkIfKeywordExists(newKeyword);

        if (isExistingKeyword) {
            return;
        }

        if (newKeyword.isSeed && keywordGroupName.length === 0) {
            const groupName = await getGroupName(newKeyword.term);
            changeKeywordGroupName(groupName);
        }
        prependKeywords([newKeyword]);
        changeQuery("");
        trackPopupClick({ event: "Add Keyword", keyword: newKeyword.term });
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        const itemsString = event.clipboardData.getData("Text");
        const items = itemsString.split(",").map((item) => item.trim());
        const newItems = items.filter((item) => !checkIfKeywordExists(item));
        const existedItems = items.filter((item) => checkIfKeywordExists(item));

        if (existedItems.length > 0) {
            const text =
                existedItems.length === 1
                    ? t("auto.keyword.list.generator.body.pase.duplicate.single", {
                          term: existedItems[0],
                      })
                    : t("auto.keyword.list.generator.body.pase.duplicate", {
                          count: existedItems.length,
                      });
            showToast(text);
        }

        const newKeywords = newItems.map((term) => createKeyword({ term, isLoading: true, isRecentlyAdded: true }));

        prependKeywords(newKeywords);
        changeQuery("");
        trackPopupClick({ event: "Paste Keywords", count: newKeywords.length });
    };

    const select = useCallback(
        (data: KeywordsVolumesResponse) =>
            loadingKeywords.map((keyword) => ({
                ...keyword,
                volume: data.Data[keyword.term]?.AverageVolume,
            })),
        [loadingKeywords],
    );

    const { data } = useKeywordsVolumes({
        keywords: loadingKeywords,
        country,
        enabled: loadingKeywords.length > 0,
        select,
    });

    useEffect(() => {
        if (data) {
            updateKeywordsVolumes(data);
        }
    }, [data, updateKeywordsVolumes]);

    useEffect(() => {
        itemsInputRef.current?.inputElem?.focus();
    }, []);

    return (
        <Flex
            alignItems="center"
            position="relative"
        >
            <Flex
                width="48px"
                justifyContent="center"
            >
                <SWReactIcons
                    iconName="search"
                    size="sm"
                />
            </Flex>
            <ListItemsInputViewWrapper>
                <ListItemsInputView
                    inputText={query}
                    inputRef={itemsInputRef}
                    onPaste={handlePaste}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                />
            </ListItemsInputViewWrapper>
        </Flex>
    );
};
